import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Table } from 'antd';
import type { TableColumnsType, ThemeConfig } from 'antd';
import { fontFamily } from '../../theme/tokens';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  useGetRolesQuery,
  useDeleteRoleMutation,
  type Role,
} from '../../features/roles/rolesApi';
import { SearchIcon, EditSquareIcon, TrashIcon, PlusIcon } from '../../assets/icons';

const TABLE_THEME: ThemeConfig = {
  token: { colorBorderSecondary: '#f3f4f6', fontFamily },
  components: {
    Table: {
      headerBg: '#ffffff',
      headerColor: '#6b7280',
      headerSplitColor: 'transparent',
      borderColor: '#f3f4f6',
      cellPaddingBlock: 24,
      cellPaddingInline: 24,
      rowHoverBg: '#f9fafb',
      colorText: '#111827',
      fontSize: 14,
    },
  },
};

const ACTION_BTN =
  'inline-flex size-8 items-center justify-center rounded-lg text-ink-secondary transition-colors hover:bg-brand-shade hover:text-brand';

export default function RolesPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const { data, isFetching } = useGetRolesQuery({ search, page, pageSize });
  const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const confirmDelete = async () => {
    if (!roleToDelete) return;
    try {
      await deleteRole(roleToDelete.id).unwrap();
      setRoleToDelete(null);
    } catch {
      /* keep the modal open so the user can retry */
    }
  };

  const columns: TableColumnsType<Role> = [
    {
      title: 'Role Name',
      key: 'name',
      width: 260,
      render: (_, r) => <span className="text-sm font-semibold text-[#111827]">{r.name}</span>,
    },
    {
      title: 'Description',
      key: 'description',
      render: (_, r) => (
        <span className="text-sm text-ink-secondary">{r.description?.trim() || '—'}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      align: 'right',
      render: (_, r) => (
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            aria-label="Edit role"
            onClick={() => navigate(`/admin/roles/${r.id}/edit`)}
            className={ACTION_BTN}
          >
            <EditSquareIcon className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Delete role"
            onClick={() => setRoleToDelete(r)}
            className="inline-flex size-8 items-center justify-center rounded-lg text-ink-secondary transition-colors hover:bg-red-50 hover:text-red-500"
          >
            <TrashIcon className="size-[18px]" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold leading-9 text-ink">Roles and Permissions</h1>
          <p className="text-sm text-ink-secondary">
            Define and manage system-wide operational capacities and access structures.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/roles/new')}
          className="flex items-center justify-center gap-2 self-start rounded-lg bg-brand px-5 py-2.5 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark sm:self-auto"
        >
          <PlusIcon className="size-5" />
          Create Role
        </button>
      </div>

      {/* Toolbar */}
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-ink-secondary/70" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search roles by name or description..."
          className="w-full rounded-lg border border-[#d1d5db] bg-white py-3 pl-11 pr-3 text-sm text-ink placeholder:text-[#6b7280] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#e5e7eb] bg-white shadow-card">
        <ConfigProvider theme={TABLE_THEME}>
          <Table<Role>
            className="chunaav-table"
            columns={columns}
            dataSource={items}
            rowKey="id"
            pagination={false}
            loading={isFetching}
            scroll={{ x: 'max-content' }}
          />
        </ConfigProvider>

        {/* Footer */}
        <Pagination
          rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
          rangeEnd={Math.min(page * pageSize, total)}
          total={total}
          noun="roles"
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>

      <ConfirmDeleteModal
        open={roleToDelete !== null}
        title="Delete Role"
        message={
          <>
            Are you sure you want to delete{' '}
            <span className="font-semibold text-ink">“{roleToDelete?.name}”</span>? This action is
            permanent and cannot be undone.
          </>
        }
        loading={isDeleting}
        onClose={() => setRoleToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
